import React, { useRef } from 'react';
import {
  formatWebDocumentAcademicYearLabel,
  isWebDocumentSchoolYearMetaRow,
  type EvaluationPlanWebDocumentData,
  getExampleAnswerTitle,
  getWebDocumentDualLabelLines,
  isWebDocumentGubunColumn,
  webDocumentColumnDisplayHeader,
} from '../../types/evaluationPlanWebDocument';
import { useEvaluationPlanWebDocumentA4Layout } from '../../utils/evaluationPlanWebDocumentA4Layout';

const META_COLGROUP = (
  <colgroup>
    <col className="cp-evaluation-plan-web-doc-col-label" />
    <col className="cp-evaluation-plan-web-doc-col-value" />
    <col className="cp-evaluation-plan-web-doc-col-label" />
    <col className="cp-evaluation-plan-web-doc-col-value" />
  </colgroup>
);

const WEB_DOC_ROW_H1 = 'cp-evaluation-plan-web-doc-row-h-1';
const WEB_DOC_ROW_H2 = 'cp-evaluation-plan-web-doc-row-h-2';

const WebDocReadOnlyCell: React.FC<{
  value: string;
  className?: string;
  centered?: boolean;
}> = ({ value, className = '', centered = false }) => (
  <div
    className={`cp-evaluation-plan-web-doc-cell-readonly${className ? ` ${className}` : ''}${centered ? ' is-centered' : ''}`}
  >
    {value || '\u00a0'}
  </div>
);

function getExampleAnswerContent(data: EvaluationPlanWebDocumentData): string {
  const current = data.exampleAnswer ?? { example: '', answer: '' };
  if (current.example && current.answer) {
    return `${current.example}\n${current.answer}`;
  }
  return current.example || current.answer || '';
}

const EvaluationPlanWebDocumentPageView: React.FC<{
  data: EvaluationPlanWebDocumentData;
  exportRootRef?: React.RefObject<HTMLDivElement | null>;
}> = ({ data, exportRootRef }) => {
  const pageRef = useRef<HTMLDivElement>(null);
  const visibleMetaRows = data.metaRows.filter((row) => !isWebDocumentSchoolYearMetaRow(row) && !row.hidden);
  const exampleAnswerContent = getExampleAnswerContent(data);

  useEvaluationPlanWebDocumentA4Layout(pageRef, [
    data.metaRows,
    data.rows,
    data.columns,
    data.title,
    exampleAnswerContent,
  ]);

  return (
    <div ref={exportRootRef} className="cp-evaluation-plan-web-doc-export-host">
      <div className="cp-evaluation-plan-web-doc-export-root">
        <div
          ref={pageRef}
          className="cp-evaluation-plan-web-doc-page cp-evaluation-plan-web-doc-page--readonly"
        >
          <div className="cp-evaluation-plan-web-doc-title-row">
            <div className="cp-evaluation-plan-web-doc-title-badge">
              <div className="cp-evaluation-plan-web-doc-title-readonly">{data.title}</div>
            </div>
          </div>

          <p className="cp-evaluation-plan-web-doc-school-year-line">{formatWebDocumentAcademicYearLabel()}</p>

          <div className="cp-evaluation-plan-web-doc-page-main">
        <table className="cp-evaluation-plan-web-doc-table cp-evaluation-plan-web-doc-meta-table">
          {META_COLGROUP}
          <tbody>
            {visibleMetaRows.map((row) => {
              if (row.layout === 'pair') {
                return (
                  <tr key={row.id} className={WEB_DOC_ROW_H1}>
                    <th className="cp-evaluation-plan-web-doc-label-cell">
                      <span className="cp-evaluation-plan-web-doc-head-label">{row.label}</span>
                    </th>
                    <td className="cp-evaluation-plan-web-doc-value-cell">
                      <WebDocReadOnlyCell value={row.value} />
                    </td>
                    <th className="cp-evaluation-plan-web-doc-label-cell">
                      <span className="cp-evaluation-plan-web-doc-head-label">{row.pairLabel ?? ''}</span>
                    </th>
                    <td className="cp-evaluation-plan-web-doc-value-cell">
                      <WebDocReadOnlyCell value={row.pairValue ?? ''} />
                    </td>
                  </tr>
                );
              }

              if (row.layout === 'dual_label') {
                const lines = getWebDocumentDualLabelLines(row);
                return (
                  <tr key={row.id} className={`${WEB_DOC_ROW_H2} cp-wd-notice-row`}>
                    <th className="cp-evaluation-plan-web-doc-label-cell">
                      <span className="cp-evaluation-plan-web-doc-dual-label">
                        {lines.map((line, index) => (
                          <span key={`${row.id}-${index}`} className="cp-evaluation-plan-web-doc-dual-label-line">
                            {line}
                          </span>
                        ))}
                      </span>
                    </th>
                    <td className="cp-evaluation-plan-web-doc-value-cell" colSpan={3}>
                      <WebDocReadOnlyCell value={row.value} />
                    </td>
                  </tr>
                );
              }

              if (!row.label && !row.isUserAdded) {
                return null;
              }

              return (
                <tr key={row.id} className={WEB_DOC_ROW_H1}>
                  <th className="cp-evaluation-plan-web-doc-label-cell">
                    <span className="cp-evaluation-plan-web-doc-head-label">{row.label}</span>
                  </th>
                  <td className="cp-evaluation-plan-web-doc-value-cell" colSpan={3}>
                    <WebDocReadOnlyCell value={row.value} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        <div className="cp-evaluation-plan-web-doc-example-answer-section">
          <div className="cp-evaluation-plan-web-doc-example-answer-header">
            <span className="cp-evaluation-plan-web-doc-head-label">{getExampleAnswerTitle(data)}</span>
          </div>
          <div className="cp-evaluation-plan-web-doc-example-answer-body">
            <WebDocReadOnlyCell
              value={exampleAnswerContent}
              className="cp-evaluation-plan-web-doc-example-answer-readonly"
            />
          </div>
        </div>

        <table className="cp-evaluation-plan-web-doc-table cp-evaluation-plan-web-doc-rubric-table">
          <colgroup>
            <col className="cp-evaluation-plan-web-doc-col-label" />
            {data.columns.slice(1).map((column) => (
              <col key={column.id} className="cp-evaluation-plan-web-doc-col-rubric-data" />
            ))}
          </colgroup>
          <thead>
            <tr className="cp-evaluation-plan-web-doc-rubric-head-row">
              {data.columns.map((column) => (
                <th
                  key={column.id}
                  className={`cp-evaluation-plan-web-doc-head-cell${isWebDocumentGubunColumn(column) ? ' cp-evaluation-plan-web-doc-gubun-col' : ''}${column.isUserAdded ? ' is-user-col' : ''}`}
                >
                  <span className="cp-evaluation-plan-web-doc-head-label cp-evaluation-plan-web-doc-head-label--multiline">
                    {webDocumentColumnDisplayHeader(column)}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.rows.map((row) => (
              <tr key={row.id} className={row.isUserAdded ? WEB_DOC_ROW_H1 : WEB_DOC_ROW_H2}>
                <th className="cp-evaluation-plan-web-doc-row-label-cell cp-evaluation-plan-web-doc-gubun-col">
                  <div className="cp-evaluation-plan-web-doc-gubun-cell-inner">
                    <span className="cp-evaluation-plan-web-doc-row-label">{row.label}</span>
                    {row.memo?.trim() ? (
                      <WebDocReadOnlyCell
                        value={row.memo}
                        className="cp-evaluation-plan-web-doc-gubun-memo-readonly"
                      />
                    ) : null}
                  </div>
                </th>
                {data.columns
                  .filter((col) => !isWebDocumentGubunColumn(col))
                  .map((column) => (
                    <td key={`${row.id}-${column.id}`} className="cp-evaluation-plan-web-doc-data-cell">
                      <WebDocReadOnlyCell value={row.cells[column.id] ?? ''} />
                    </td>
                  ))}
              </tr>
            ))}
          </tbody>
        </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EvaluationPlanWebDocumentPageView;
